/**
 * useContentEditor Hook
 * 
 * Universal hook for content editing across all platforms (LinkedIn, Twitter, Facebook).
 * Provides a unified interface for:
 * - Content state management
 * - Tool-driven orchestration via OpenAI function calling
 * - AI generation/editing with appropriate parameters
 * - Response validation and sanitization
 * 
 * @module useContentEditor
 */

import { useState, useCallback, useRef, useReducer, useEffect } from 'react';
import {
    createContentState,
    contentStateReducer,
    contentSelectors,
    contentActions,
    calculateContentStats
} from '../utils/contentStateManager';

import {
    validateEditResponse,
    sanitizeEditResponse,
    createValidationReport
} from '../utils/responseValidator';
import repurposeAiService from '../services/repurposeAiService';

/**
 * Hook configuration options
 * @typedef {object} UseContentEditorConfig
 * @property {string} platform - Platform identifier (linkedin, twitter, facebook)
 * @property {object} context - Episode and plan context
 * @property {function} onContentChange - Callback when content changes
 * @property {string} initialContent - Optional initial content
 * @property {boolean} autoValidate - Whether to auto-validate edit responses
 * @property {function} onValidationWarning - Callback for validation warnings
 */

/**
 * useContentEditor hook
 * 
 * @param {UseContentEditorConfig} config - Configuration options
 * @returns {object} Hook state and actions
 */
export const useContentEditor = ({
    platform = 'linkedin',
    context = {},
    onContentChange,
    initialContent = null,
    autoValidate = true,
    onValidationWarning
}) => {
    // Content state management
    const [state, dispatch] = useReducer(
        contentStateReducer,
        null,
        () => createContentState(initialContent)
    );

    // Streaming and loading state
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState(null);
    const [activeTool, setActiveTool] = useState(null); // Track the currently running tool for the UI
    const [lastValidation, setLastValidation] = useState(null);

    // Refs for stable callbacks
    const streamBufferRef = useRef('');
    const onContentChangeRef = useRef(onContentChange);
    const onValidationWarningRef = useRef(onValidationWarning);

    // Keep refs updated
    useEffect(() => {
        onContentChangeRef.current = onContentChange;
    }, [onContentChange]);

    useEffect(() => {
        onValidationWarningRef.current = onValidationWarning;
    }, [onValidationWarning]);

    /**
     * Process a user request using the orchestrator
     * Routes to the appropriate tool based on OpenAI function calling
     * 
     * @param {string} userRequest - User's instruction/request
     * @param {object} options - Additional options
     * @param {function} options.onToken - Token callback for streaming
     * @param {function} options.onComplete - Completion callback
     * @param {function} options.onError - Error callback
     * @param {string} options.contentType - Content type (post, coming_soon)
     */
    const processRequest = useCallback(async (userRequest, options = {}) => {
        const { onToken, onComplete, onError, contentType } = options;

        console.log('[useContentEditor] Processing request with orchestrator:', {
            platform,
            userRequest: userRequest.substring(0, 50),
            contentType
        });

        dispatch(contentActions.setOperation({ mode: 'processing' }));
        setIsStreaming(true);
        setError(null);
        streamBufferRef.current = '';

        try {
            let activeToolRef = null;

            // Use the orchestrator endpoint for tool-driven routing
            const orchestrationResult = await repurposeAiService.orchestrate({
                message: userRequest,
                platform: platform || 'linkedin',
                contentType: contentType || 'post',
                context: {
                    episodeId: context?.episodeId,
                    guestName: context?.guestName,
                    guestBio: context?.guestBio,
                    transcript: context?.transcript,
                    planContent: context?.planContent,
                    topics: context?.topics,
                    currentContent: state.currentContent
                },
                onToolStart: (data) => {
                    const toolName = data?.tool || data?.name || null;
                    activeToolRef = toolName;
                    setActiveTool(toolName);
                    options.onToolStart?.(data);
                },
                onToolComplete: (data) => {
                    activeToolRef = null;
                    setActiveTool(null);
                    options.onToolComplete?.(data);
                },
                onToken: (token) => {
                    const isContentTool = ['generate_post', 'modify_post', 'generate_caption', 'modify_caption'].includes(activeToolRef);

                    if (isContentTool) {
                        // Stream directly to the UI preview modal
                        streamBufferRef.current += token;
                        dispatch(contentActions.setContent(streamBufferRef.current));
                        onContentChangeRef.current?.(streamBufferRef.current);
                    } else {
                        // Stream to the chat bubble (conversational text)
                        onToken?.(token);
                    }
                }
            });

            console.log('[useContentEditor] Orchestration result:', {
                tool: orchestrationResult.tool,
                actionType: orchestrationResult.action?.type,
                hasGeneratedContent: !!orchestrationResult.action?.generatedContent
            });

            // Handle based on the tool that was selected
            switch (orchestrationResult.tool) {
                case 'conversational': {
                    // Handle conversation - no content modification
                    const responseText = orchestrationResult.parameters.response;

                    if (onToken && responseText) {
                        const words = responseText.split(' ');
                        for (let i = 0; i < words.length; i++) {
                            onToken(words[i] + (i < words.length - 1 ? ' ' : ''));
                        }
                    }

                    setIsStreaming(false);
                    onComplete?.({
                        content: null,
                        mode: { mode: 'conversational', platformIntent: 'conversational' },
                        advisoryText: responseText,
                        responseType: 'conversation',
                        validation: null
                    });
                    return;
                }

                case 'content_generation': {
                    // Check if content was already generated by the agent
                    const generatedContent = orchestrationResult.action?.generatedContent;

                    if (generatedContent) {
                        dispatch(contentActions.setContent(generatedContent));
                        onContentChangeRef.current?.(generatedContent);
                        setIsStreaming(false);
                        onComplete?.({
                            content: generatedContent,
                            mode: { mode: 'generation', platformIntent: 'content_generation' },
                            validation: null
                        });
                        return;
                    }

                    // Fallback: Use streaming generation if agent didn't generate content
                    await repurposeAiService.streamLinkedInGeneration({
                        prompt: userRequest,
                        context,
                        conversationHistory: [],
                        platform,
                        options: {
                            tone: orchestrationResult.parameters.tone,
                            length: orchestrationResult.parameters.length
                        },
                        onToken: (token) => {
                            streamBufferRef.current += token;
                            dispatch(contentActions.setContent(streamBufferRef.current));
                            onContentChangeRef.current?.(streamBufferRef.current);
                        },
                        onComplete: (result) => {
                            const content = result.post || streamBufferRef.current;
                            dispatch(contentActions.setContent(content));
                            onContentChangeRef.current?.(content);
                            setIsStreaming(false);
                            onComplete?.({
                                content,
                                mode: { mode: 'generation', platformIntent: 'content_generation' },
                                validation: null
                            });
                        },
                        onError: (err) => {
                            setError(err.message);
                            setIsStreaming(false);
                            onError?.(err);
                        }
                    });
                    return;
                }

                case 'content_edit': {
                    // Check if content was already modified by the agent
                    const modifiedContent = orchestrationResult.action?.generatedContent;

                    if (modifiedContent) {
                        dispatch(contentActions.setContent(modifiedContent));
                        onContentChangeRef.current?.(modifiedContent);
                        setIsStreaming(false);
                        onComplete?.({
                            content: modifiedContent,
                            mode: { mode: 'edit', platformIntent: 'content_adjustment' },
                            editType: orchestrationResult.parameters.edit_type,
                            validation: null
                        });
                        return;
                    }

                    // Fallback: Handle content editing via streaming
                    if (!state.currentContent) {
                        setIsStreaming(false);
                        onError?.(new Error('No content to edit'));
                        return;
                    }

                    await repurposeAiService.streamEdit({
                        sourceText: state.currentContent,
                        instruction: orchestrationResult.parameters.instruction,
                        context,
                        platform,
                        mode: 'edit',
                        onToken: (token) => {
                            streamBufferRef.current += token;
                            dispatch(contentActions.setContent(streamBufferRef.current));
                            onContentChangeRef.current?.(streamBufferRef.current);
                        },
                        onComplete: (result) => {
                            let content = result.text || streamBufferRef.current;

                            // Sanitize response
                            content = sanitizeEditResponse(content);

                            // Validate if enabled
                            let validation = null;
                            if (autoValidate && state.currentContent) {
                                validation = validateEditResponse(content, state.currentContent);
                                setLastValidation(validation);

                                if (validation.warnings?.length > 0) {
                                    console.warn('[useContentEditor] Validation warnings:', validation.warnings);
                                    onValidationWarningRef.current?.(validation.warnings);
                                }
                            }

                            dispatch(contentActions.setContent(content));
                            onContentChangeRef.current?.(content);
                            setIsStreaming(false);
                            onComplete?.({
                                content,
                                mode: { mode: 'edit', platformIntent: 'content_adjustment' },
                                editType: orchestrationResult.parameters.edit_type,
                                validation
                            });
                        },
                        onError: (err) => {
                            setError(err.message);
                            setIsStreaming(false);
                            onError?.(err);
                        }
                    });
                    return;
                }

                case 'coming_soon_poster':
                case 'image_edit': {
                    // These are handled by the coming soon service
                    setIsStreaming(false);
                    onComplete?.({
                        content: null,
                        mode: { mode: 'poster', platformIntent: orchestrationResult.tool },
                        action: orchestrationResult.action,
                        validation: null
                    });
                    return;
                }

                default: {
                    // Unknown tool - treat as conversational
                    const responseText = orchestrationResult.parameters?.response ||
                        "I'm not sure how to help with that. Could you tell me more?";

                    if (onToken) {
                        const words = responseText.split(' ');
                        for (let i = 0; i < words.length; i++) {
                            onToken(words[i] + (i < words.length - 1 ? ' ' : ''));
                        }
                    }

                    setIsStreaming(false);
                    onComplete?.({
                        content: null,
                        mode: { mode: 'conversational', platformIntent: 'conversational' },
                        advisoryText: responseText,
                        validation: null
                    });
                }
            }

        } catch (err) {
            console.error('[useContentEditor] Orchestration error:', err);
            setError(err.message);
            setIsStreaming(false);
            onError?.(err);
        }
    }, [platform, context, state.currentContent, autoValidate]);

    /**
     * Perform a generation operation (explicit)
     * 
     * @param {string} prompt - Generation prompt
     * @param {object} options - Additional options
     */
    const generate = useCallback(async (prompt, options = {}) => {
        return processRequest(prompt, { ...options, forceMode: 'generation' });
    }, [processRequest]);

    /**
     * Perform an edit operation (explicit)
     * 
     * @param {string} instruction - Edit instruction
     * @param {object} options - Additional options
     */
    const edit = useCallback(async (instruction, options = {}) => {
        if (!state.currentContent) {
            const err = new Error('No content to edit. Use generate() first.');
            setError(err.message);
            options.onError?.(err);
            return;
        }
        return processRequest(instruction, { ...options, forceMode: 'edit' });
    }, [state.currentContent, processRequest]);

    /**
     * Commit current changes (mark as saved)
     */
    const commitChanges = useCallback(() => {
        dispatch(contentActions.commitChanges());
    }, []);

    /**
     * Reset to original content
     */
    const reset = useCallback(() => {
        dispatch(contentActions.reset());
        onContentChangeRef.current?.(state.originalContent);
    }, [state.originalContent]);

    /**
     * Manually set content
     * 
     * @param {string} content - New content
     */
    const setContent = useCallback((content) => {
        dispatch(contentActions.setContent(content));
        onContentChangeRef.current?.(content);
    }, []);

    /**
     * Clear content and state
     */
    const clear = useCallback(() => {
        dispatch(contentActions.setContent(null));
        setError(null);
        setLastValidation(null);
        onContentChangeRef.current?.(null);
    }, []);

    /**
     * Undo to previous version from history
     * 
     * @param {number} index - History index (default: last)
     */
    const undo = useCallback((index) => {
        const historyIndex = index ?? state.operationHistory.length - 1;
        dispatch(contentActions.restoreFromHistory(historyIndex));
        onContentChangeRef.current?.(state.currentContent);
    }, [state.operationHistory, state.currentContent]);

    /**
     * Detect mode for a given request (without executing)
     * Returns a simple analysis of what the request might entail
     * 
     * @param {string} request - User request
     * @returns {object} Detection result
     */
    const detectMode = useCallback((request) => {
        // Simple heuristic-based detection for UI purposes
        const requestLower = (request || '').toLowerCase().trim();

        // Check for greetings/conversational
        const greetings = ['hi', 'hey', 'hello', 'howdy', 'greetings', 'good morning', 'good afternoon'];
        if (greetings.some(g => requestLower === g || requestLower.startsWith(g + ' '))) {
            return {
                mode: 'conversational',
                confidence: 0.95,
                reason: 'Greeting detected'
            };
        }

        // Check for generation keywords
        const generationKeywords = ['create', 'write', 'generate', 'make a post', 'draft', 'new post'];
        if (generationKeywords.some(kw => requestLower.includes(kw))) {
            return {
                mode: 'generation',
                confidence: 0.85,
                reason: 'Generation keyword detected'
            };
        }

        // Check for edit keywords
        const editKeywords = ['change', 'modify', 'update', 'edit', 'shorten', 'lengthen', 'fix'];
        if (editKeywords.some(kw => requestLower.includes(kw))) {
            return {
                mode: 'edit',
                confidence: 0.85,
                reason: 'Edit keyword detected'
            };
        }

        // Default based on content existence
        return {
            mode: state.currentContent ? 'edit' : 'generation',
            confidence: 0.6,
            reason: state.currentContent ? 'Existing content present' : 'No existing content'
        };
    }, [state.currentContent]);

    /**
     * Get content statistics
     */
    const getStats = useCallback(() => {
        return calculateContentStats(state.currentContent);
    }, [state.currentContent]);

    // Derived state
    const hasContent = contentSelectors.hasContent(state);
    const canUndo = contentSelectors.canUndo(state);

    return {
        // State
        content: state.currentContent,
        originalContent: state.originalContent,
        isDirty: state.isDirty,
        isStreaming,
        error,
        activeTool,
        lastOperation: state.lastOperation,
        lastValidation,
        hasContent,
        canUndo,
        historyLength: state.operationHistory.length,

        // Actions
        processRequest,
        generate,
        edit,
        commitChanges,
        reset,
        setContent,
        clear,
        undo,

        // Utilities
        detectMode,
        getStats,

        // Advanced
        dispatch, // Expose for custom actions if needed
        state // Full state for advanced use cases
    };
};

export default useContentEditor;
